import { createApi } from "@reduxjs/toolkit/query/react";
import axiosBaseQuery from "../../config/service";
import { HMAC_SECRET_KEY } from "../../config/api.config";
import CryptoJS from "crypto-js";

export const propertyApiService = createApi({
  reducerPath: "properyApiService",
  baseQuery: axiosBaseQuery(),
  endpoints: (builder) => ({
    fetchModifiedProperty: builder.query({
      query: (propertyId) => {
        const token = JSON.parse(localStorage.getItem("token"));

        return {
          url: `api/property/${propertyId}/modified`,
          method: "GET",
          headers: { "x-auth-token": token },
        };
      },
      providesTags: ["fetchUserOrders"],
    }),

    fetchMasterProperties: builder.mutation({
      query: ({ body }) => {
        const token = JSON.parse(localStorage.getItem("token"));
        return {
          url: `api/property/master/search`,
          method: "POST",
          headers: { "x-auth-token": token },
          data: body,
        };
      },
    }),
    getSingleMasterProperty: builder.query({
      query: ({ propertyId }) => {
        const token = JSON.parse(localStorage.getItem("token"));
        return {
          url: `api/property/master/single/${propertyId}`,
          method: "GET",
          headers: { "x-auth-token": token },
        };
      },
    }),
    linkPropertyMasterDocuments: builder.mutation({
      query: () => {},
    }),
    linkMasterPropertyImages: builder.mutation({
      query: ({ propertyId, masterPropertyId }) => {
        const token = JSON.parse(localStorage.getItem("token"));

        return {
          url: `api/property/master/link/media`,
          method: "POST",
          headers: { "x-auth-token": token },
          data: { propertyId, masterPropertyId },
        };
      },
    }),
    linkMasterPropertyDocuments: builder.mutation({
      query: ({ body }) => {
        const token = JSON.parse(localStorage.getItem("token"));
        const bodyJson = JSON.stringify(body);

        const hmac = CryptoJS.HmacSHA256(bodyJson, HMAC_SECRET_KEY).toString();

        return {
          url: `api/property/master/link/document`,
          method: "POST",
          headers: { "x-auth-token": token, "x-signature": hmac },
          data: body,
        };
      },
    }),
    getPropertyCategoryReference: builder.query({
      query: () => {
        return {
          // Leading slash matters, without it axios resolves this
          // relative to the current page URL (e.g. on the locker
          // page → `/locker/my-properties/api/property/categories`
          // → 404). The sibling `getCategory` query already has the
          // slash and works; this one was missing it post-revamp.
          // Auth is auto-injected as `x-access-token` by the axios
          // interceptor in src/config/interceptor.js.
          url: `/api/property/categories`,
          method: "GET",
        };
      },
    }),

    // https://dev.jaaga.ai/api/property/static/states/telangana/non-agriculture/property/info.json

    getBillsJson: builder.query({
      query: ({ state, propertyType }) => {
        return {
          url: `/api/property/static/states/${state}/${propertyType}/property/bills.json`,
          method: "GET",
        };
      },
    }),

    getBillAmount: builder.mutation({
      query: ({ propertyId, endpoint, body }) => {
        const token = JSON.parse(localStorage.getItem("token"));
        return {
          // No property means a plain check: the backend returns the bill without saving it.
          url: propertyId
            ? `/api/property/bills/${propertyId}/${endpoint}`
            : `/api/property/bills/${endpoint}`,
          method: "POST",
          headers: { "x-auth-token": token },
          data: body,
        };
      },
    }),
    sendDocumentOTP: builder.mutation({
      query: ({ body }) => {
        const token = JSON.parse(localStorage.getItem("token"));
        return {
          url: `api/property/state/sendOTP`,
          method: "POST",
          headers: { "x-auth-token": token },
          data: body,
        };
      },
    }),
    verifyDocumentOTP: builder.mutation({
      query: ({ body }) => {
        const token = JSON.parse(localStorage.getItem("token"));
        return {
          url: `api/property/state/verifyOTP`,
          method: "POST",
          headers: { "x-auth-token": token },
          data: body,
        };
      },
    }),
    getCategory: builder.query({
      query: () => {
        return {
          url: `/api/property/categories`,
          method: "GET",
        };
      },
    }),
    // Instant EC: start sends the OTP to the mobile, otp verifies + fetches the
    // EC and auto-completes the order. `base` picks the state's flow —
    // "ts-ec" (Telangana, default) or "ap-ec" (Andhra Pradesh); same contract.
    startTsEc: builder.mutation({
      query: ({ body, base = "ts-ec" }) => {
        const token = JSON.parse(localStorage.getItem("token"));
        return {
          url: `api/property/${base}/start`,
          method: "POST",
          headers: { "x-auth-token": token },
          data: body,
        };
      },
    }),
    verifyTsEcOtp: builder.mutation({
      query: ({ body, base = "ts-ec" }) => {
        const token = JSON.parse(localStorage.getItem("token"));
        return {
          url: `api/property/${base}/otp`,
          method: "POST",
          headers: { "x-auth-token": token },
          data: body,
        };
      },
    }),
  }),
});

export const {
  useFetchModifiedPropertyQuery,
  useFetchMasterPropertiesMutation,
  useGetSingleMasterPropertyQuery,
  useLazyGetSingleMasterPropertyQuery,
  useLinkMasterPropertyImagesMutation,
  useLinkMasterPropertyDocumentsMutation,
  useGetPropertyCategoryReferenceQuery,
  useGetBillsJsonQuery,
  useGetBillAmountMutation,
  useGetCategoryQuery,
  useSendDocumentOTPMutation,
  useVerifyDocumentOTPMutation,
  useStartTsEcMutation,
  useVerifyTsEcOtpMutation
} = propertyApiService;
